import React, { useEffect, useRef } from 'react';
import { Shield } from 'lucide-react';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import Disclaimer from '../ui/Disclaimer';
import { useHubspot } from '../../hooks/useHubspot';

type SSNVerificationFormProps = {
  formData: FormData;
  loading: boolean;
  error: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function SSNVerificationForm({
  formData,
  loading,
  error,
  onChange,
  onSubmit
}: SSNVerificationFormProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hubspot = useHubspot();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.ssnLast4 && formData.ssnLast4.length === 4) {
      // Track SSN verification in HubSpot
      hubspot.trackEvent('ssn_verification_submitted', {
        email: formData.email,
        form_step: 'ssn_verification'
      });

      onSubmit({ ssnLast4: formData.ssnLast4 });
    }
  };

  const isValidLast4SSN = (ssn: string | undefined) => {
    return ssn ? /^\d{4}$/.test(ssn) : false;
  };

  return (
    <form id="ssn-verification-form" name="ssn-verification-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center">
        <Title 
          id="ssn-verification-title"
          name="ssn-verification-title"
          as="h1" 
          className="mb-2"
        >
          Verify Your Identity
        </Title>
        <Description 
          id="ssn-verification-description"
          name="ssn-verification-description"
          size="lg"
        >
          Enter the last 4 digits of your Social Security Number
        </Description>
      </div>

      <div className="space-y-4">
        <div>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              name="ssnLast4"
              id="ssn-verification-input"
              value={formData.ssnLast4 || ''}
              onChange={onChange}
              maxLength={4}
              placeholder="Last 4 digits"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#b3905e] focus:ring-2 focus:ring-[#b3905e]/20 transition duration-200 text-center text-2xl tracking-widest"
              required
              inputMode="numeric"
            />
          </div>
          <div className="mt-4 flex items-start gap-3 text-sm text-gray-600 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <Shield className="w-5 h-5 flex-shrink-0 text-[#b3905e] mt-0.5" />
            <p>
              We use your social security number to validate your information and check your credit. 
              This will NOT affect your credit score. Your information is transmitted securely.
            </p>
          </div>
        </div>
      </div>

      <ErrorMessage message={error} />
      
      <Button
        id="ssn-verification-submit"
        name="ssn-verification-submit"
        type="submit"
        disabled={loading || !isValidLast4SSN(formData.ssnLast4 || '')}
        loading={loading}
        loadingText="Submitting..."
        fullWidth
        size="lg"
      >
        Submit Application
      </Button>

      <Disclaimer
        id="ssn-verification-disclaimer"
        name="ssn-verification-disclaimer"
      >
        <p className="mb-3">By clicking "Submit Application" above:</p>
        <p className="mb-3">
          I consent to Symple Lending submitting my application to its <a href="https://symplelending.com/partners" target="_blank" rel="noopener noreferrer">Partners</a> to evaluate my eligibility for personal loan offers and related financial products and services.
        </p>
        <p>
          I acknowledge that each <a href="https://symplelending.com/partners" target="_blank" rel="noopener noreferrer">Partner</a> has its own privacy practices and consent terms.   To view the full list of <a href="https://symplelending.com/partners" target="_blank" rel="noopener noreferrer">Partners</a> and their consent language, please visit <a href="https://symplelending.com/partners" target="_blank" rel="noopener noreferrer">Lending Partners’ Consents and Disclosures</a>.
        </p>
      </Disclaimer>
    </form>
  );
}